import { Injectable } from '@angular/core';
import {
  NativeBiometric,
  BiometryType,
} from '@capgo/capacitor-native-biometric';
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root',
})
export class BiometricService {
  // 🔧 헬퍼 메소드: 플랫폼별 키 생성 로직을 중앙화
  private static getCredentialsKey = (platform: string) =>
    `biometric_credentials_${platform}`;

  private static getAutoBiometricKey = (platform: string) =>
    `auto_biometric_enabled_${platform}`;

  // 현재 플랫폼의 키를 반환하는 getter
  private get CREDENTIALS_KEY(): string {
    return BiometricService.getCredentialsKey(Capacitor.getPlatform());
  }

  private get AUTO_BIOMETRIC_KEY(): string {
    return BiometricService.getAutoBiometricKey(Capacitor.getPlatform());
  }

  /**
   * 바이오 인증 가용성을 확인합니다.
   */
  async isAvailable(): Promise<boolean> {
    try {
      const result = await NativeBiometric.isAvailable();
      return result.isAvailable;
    } catch (error) {
      console.error('Biometric availability check failed', error);
      return false;
    }
  }

  /**
   * 기기에서 지원하는 바이오 인증 타입을 조회합니다.
   */
  async getBiometryType(): Promise<BiometryType | null> {
    try {
      const result = await NativeBiometric.isAvailable();
      return result.biometryType || null;
    } catch {
      return null;
    }
  }

  /**
   * 자격 증명을 저장합니다. (Preferences 사용)
   * 저장 전 바이오 인증으로 사용자를 확인합니다.
   * @param autoLogin 자동 로그인 활성화 여부 (앱 시작 시 자동 바이오 팝업 표시)
   */
  async setCredentials(
    phone: string,
    pin: string,
    autoLogin = false
  ): Promise<void> {
    // 먼저 바이오 인증으로 사용자 확인
    await NativeBiometric.verifyIdentity({
      reason: '바이오 인증을 등록합니다',
      title: '바이오 인증 등록',
      subtitle: '지문 또는 얼굴을 인식해주세요',
      useFallback: true,
    });

    // ⭐️ iOS Keychain에 자격 증명 저장 (NativeBiometric 사용)
    try {
      await NativeBiometric.setCredentials({
        username: phone,
        password: pin,
        server: this.CREDENTIALS_KEY,
      });
    } catch (error) {
      // Android 등 특정 환경에서 Keystore 저장 실패 시 경고만 남기고 진행 (Preferences가 보완함)
      console.warn('NativeBiometric.setCredentials failed', error);
    }

    // Preferences에도 자격 증명 저장 (백업용)
    const credentials = JSON.stringify({ phone, pin });
    await Preferences.set({
      key: this.CREDENTIALS_KEY,
      value: credentials,
    });

    // 자동 바이오 로그인 활성화 여부 저장
    await this.setAutoBiometricEnabled(autoLogin);
  }

  /**
   * 바이오 인증 팝업을 띄우고 자격 증명을 조회합니다.
   */
  async getCredentials(): Promise<{ phone: string; pin: string } | null> {
    try {
      const available = await this.isAvailable();
      if (!available) return null;

      // 저장된 자격 증명이 있는지 확인
      const { value } = await Preferences.get({ key: this.CREDENTIALS_KEY });
      if (!value) return null;

      // 바이오 인증 수행
      await NativeBiometric.verifyIdentity({
        reason: '로그인을 위해 인증해주세요',
        title: '바이오 인증 로그인',
        subtitle: '지문 또는 얼굴을 인식해주세요',
        useFallback: true,
      });

      // 인증 성공 시 자격 증명 반환
      const credentials = JSON.parse(value);
      return {
        phone: credentials.phone,
        pin: credentials.pin,
      };
    } catch (error) {
      console.warn('Biometric authentication failed or canceled', error);
      return null;
    }
  }

  /**
   * 저장된 자격 증명을 삭제합니다.
   */
  async deleteCredentials(): Promise<void> {
    try {
      // ⭐️ iOS Keychain에서 실제 자격증명 삭제
      await NativeBiometric.deleteCredentials({
        server: this.CREDENTIALS_KEY,
      });
    } catch (error) {
      console.warn('NativeBiometric.deleteCredentials failed', error);
      // Keychain에 데이터가 없어도 에러가 발생할 수 있으므로 무시
    }

    // Preferences에서도 삭제
    await Preferences.remove({ key: this.CREDENTIALS_KEY });
    await Preferences.remove({ key: this.AUTO_BIOMETRIC_KEY });
  }

  /**
   * 자격 증명이 저장되어 있는지 확인합니다.
   */
  async hasCredentials(): Promise<boolean> {
    const { value } = await Preferences.get({ key: this.CREDENTIALS_KEY });
    return !!value;
  }

  /**
   * 자동 바이오 로그인이 활성화되어 있는지 확인합니다.
   * (자동로그인 체크 + 바이오 인증 등록된 경우에만 true)
   */
  async isAutoBiometricEnabled(): Promise<boolean> {
    const { value } = await Preferences.get({ key: this.AUTO_BIOMETRIC_KEY });
    return value === 'true';
  }

  /**
   * 자동 바이오 로그인 활성화 여부를 설정합니다.
   */
  async setAutoBiometricEnabled(enabled: boolean): Promise<void> {
    await Preferences.set({
      key: this.AUTO_BIOMETRIC_KEY,
      value: enabled ? 'true' : 'false',
    });
  }

  /**
   * 개발/테스트용: 모든 플랫폼의 저장된 데이터 삭제
   */
  async clearAllPlatformData(): Promise<void> {
    // ⭐️ 현재 플랫폼의 Keychain/Keystore 데이터 삭제
    try {
      await NativeBiometric.deleteCredentials({
        server: this.CREDENTIALS_KEY,
      });
    } catch (error) {
      console.warn('Clear native biometric failed', error);
    }

    // 모든 플랫폼의 Preferences 데이터 삭제
    const platforms = ['ios', 'android', 'web'];
    for (const platform of platforms) {
      await Preferences.remove({
        key: BiometricService.getCredentialsKey(platform),
      });
      await Preferences.remove({
        key: BiometricService.getAutoBiometricKey(platform),
      });
    }
  }
}
